import React, { useRef, useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import { de } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useSchedule } from '../context/ScheduleContext';
import { useAuth } from '../context/AuthContext';
import '../styles/neumorphism.css';

const { FiAlertTriangle, FiPrinter, FiDownload, FiX, FiCheck, FiUsers } = FiIcons;

// Defined SHIFTS constant to fix "SHIFTS is not defined" error
const SHIFTS = ['früh', 'zwischen', 'spät', 'holiday'];

function VerticalScheduleView({ schedule, employees, settings }) {
  const printRef = useRef();
  const { dispatch } = useSchedule();
  const { currentDepartment } = useAuth();
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editedHours, setEditedHours] = useState({});
  const [targetHours, setTargetHours] = useState({});

  useEffect(() => {
    if (schedule) {
      setEditedHours(schedule.employeeHours || {});
      setTargetHours(schedule.targetHours || {});
    }
  }, [schedule]);

  if (!schedule) return null;

  const monthDate = new Date(schedule.month + '-01');
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const daysWithoutZwischendienst = schedule.daysWithoutZwischendienst || [];

  const getShiftCode = (shift) => {
    switch (shift) {
      case 'früh':
        return '1';
      case 'zwischen':
        return '2';
      case 'spät':
        return '3';
      case 'holiday':
        return 'F';
      default:
        return '-';
    }
  };

  const getEmployeeShiftForDay = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const daySchedule = schedule.schedule[dateStr] || {};

    if (daySchedule.früh && daySchedule.früh.includes(employeeId)) {
      return 'früh';
    }
    if (daySchedule.zwischen && daySchedule.zwischen.includes(employeeId)) {
      return 'zwischen';
    }
    if (daySchedule.spät && daySchedule.spät.includes(employeeId)) {
      return 'spät';
    }
    if (daySchedule.holiday && daySchedule.holiday.includes(employeeId)) {
      return 'holiday';
    }
    return null;
  };

  const getEmployeePreferenceForDay = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const employee = employees.find((emp) => emp.id === employeeId);
    if (!employee || !employee.preferences) return null;
    const preference = employee.preferences.find((p) => p.date === dateStr);
    return preference || null;
  };

  const getShiftCellClass = (shift, hasPreference = false, preferenceType = null) => {
    let baseClass = '';
    switch (shift) {
      case 'früh':
        baseClass = 'bg-blue-500 text-white';
        break;
      case 'zwischen':
        baseClass = 'bg-green-500 text-white';
        break;
      case 'spät':
        baseClass = 'bg-purple-500 text-white';
        break;
      case 'holiday':
        baseClass = 'bg-yellow-400 text-black';
        break;
      default:
        baseClass = 'bg-gray-100 text-gray-500';
    }

    if (hasPreference) {
      if (preferenceType === 'frei') {
        baseClass += ' border-2 border-red-400';
      } else if (preferenceType === shift) {
        baseClass += ' border-2 border-yellow-400';
      } else if (preferenceType && preferenceType !== shift) {
        baseClass += ' border-2 border-orange-400';
      }
    }
    return baseClass;
  };

  const handleCellClick = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedCell({ employeeId, dateStr, date });
    setShowEmployeeModal(true);
  };

  const formatHours = (totalHours) => {
    if (typeof totalHours !== 'number' || isNaN(totalHours)) {
      return '0h 0m';
    }
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const assignShiftToEmployee = (shift) => {
    if (!selectedCell) return;
    const { employeeId, dateStr } = selectedCell;
    const employee = employees.find((e) => e.id === employeeId);

    // Create a new schedule object with the changes
    const newSchedule = { ...schedule };
    const newHours = { ...editedHours };

    if (!newSchedule.schedule[dateStr]) {
      newSchedule.schedule[dateStr] = {};
    }

    // Remove from previous shift
    const previousShift = getEmployeeShiftForDay(employeeId, new Date(dateStr));
    if (previousShift) {
      const shiftDuration =
        previousShift === 'holiday'
          ? (8 + 24 / 60) * (employee.workload / 100)
          : settings.shifts[previousShift]?.hours + settings.shifts[previousShift]?.minutes / 60 || 0;
      newHours[employeeId] -= shiftDuration;

      ['früh', 'zwischen', 'spät', 'holiday'].forEach((shiftType) => {
        if (newSchedule.schedule[dateStr][shiftType]) {
          newSchedule.schedule[dateStr][shiftType] = newSchedule.schedule[dateStr][shiftType].filter(
            (id) => id !== employeeId
          );
          if (newSchedule.schedule[dateStr][shiftType].length === 0) {
            delete newSchedule.schedule[dateStr][shiftType];
          }
        }
      });
    }

    // Add to new shift
    if (shift !== 'none') {
      if (!newSchedule.schedule[dateStr][shift]) {
        newSchedule.schedule[dateStr][shift] = [];
      }
      newSchedule.schedule[dateStr][shift].push(employeeId);

      const shiftDuration =
        shift === 'holiday'
          ? (8 + 24 / 60) * (employee.workload / 100)
          : settings.shifts[shift]?.hours + settings.shifts[shift]?.minutes / 60 || 0;

      newHours[employeeId] = (newHours[employeeId] || 0) + shiftDuration;
    }

    // Update state (no persistence)
    dispatch({ type: 'SET_CURRENT_SCHEDULE', payload: newSchedule });
    setEditedHours(newHours);
    setShowEmployeeModal(false);
    setSelectedCell(null);
  };

  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = 'print-styles';
    style.innerHTML = `
      @page { size: A4 portrait; margin: 0.5cm; }
      @media print {
        body * { visibility: hidden; }
        #print-content, #print-content * { visibility: visible; }
        #print-content { position: absolute; left: 0; top: 0; width: 100%; }
        .neu-card, .neu-element, .neu-element-inset { box-shadow: none !important; border: 1px solid #eee; }
        table { page-break-inside: auto; border-collapse: collapse; font-size: 6pt !important; }
        th, td { padding: 1px !important; }
        .w-8, .h-8 { width: 12px !important; height: 12px !important; min-width: 12px !important; min-height: 12px !important; }
        .text-sm, .text-xs, .text-xxs { font-size: 6pt !important; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        @page {
          @bottom-center { content: "Seite " counter(page) " von " counter(pages); }
          @top-center { content: "Dienstplan ${format(monthDate, 'MMMM yyyy', { locale: de })}"; }
        }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => {
      const printStyle = document.getElementById('print-styles');
      if (printStyle) {
        printStyle.remove();
      }
    }, 1000);
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById('print-content');
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`Dienstplan_${format(monthDate, 'yyyy-MM', { locale: de })}_Vertikal.pdf`);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim Erstellen der PDF-Datei');
    }
  };

  const isHoliday = (date) => {
    if (!settings.holidays || !settings.holidays.length) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    return settings.holidays.some((holiday) => holiday.date === dateStr);
  };

  const isUnderstaffed = (date) => {
    if (!schedule) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    const isDayWeekend = isWeekend(date);
    const staffingRules = isDayWeekend ? settings.minStaffing.weekend : settings.minStaffing.weekday;
    const daySchedule = schedule.schedule[dateStr] || {};
    const wasZwischendienstOriginallyOmitted = (schedule.daysWithoutZwischendienst || []).includes(dateStr);

    for (const shift of ['früh', 'zwischen', 'spät']) {
      const required = staffingRules[shift] || 0;
      if (required === 0) continue;
      const assigned = daySchedule[shift]?.length || 0;

      if (shift === 'zwischen') {
        if (wasZwischendienstOriginallyOmitted && assigned === 0) {
          continue;
        }
      }

      if (assigned < required) {
        return true;
      }
    }
    return false;
  };

  const getMainExplanationReason = () => {
    const explanations = schedule.explanations || [];
    if (!explanations || explanations.length === 0) return null;
    const staffingShortage = explanations.find((exp) => exp.type === 'staffing_shortage');
    if (staffingShortage) {
      return 'Zu wenig Gesamtressourcen';
    }
    const tooManyFreeWishes = explanations.find((exp) => exp.type === 'many_free_preferences');
    if (tooManyFreeWishes) {
      return 'Zu viele Freiwünsche';
    }
    const highSickLeave = explanations.find((exp) => exp.type === 'high_sick_leave');
    if (highSickLeave) {
      return 'Zu viele Krankheitstage';
    }
    const skillShortage = explanations.find((exp) => exp.type === 'skill_shortage');
    if (skillShortage) {
      return 'Mangel an Qualifikationen';
    }
    const weekendConstraint = explanations.find((exp) => exp.type === 'weekend_constraint');
    if (weekendConstraint) {
      return 'Zu strikte Wochenendregeln';
    }
    return 'Mehrere Einschränkungen';
  };

  const mainReason = getMainExplanationReason();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-2 print:hidden">
        <h2 className="text-xl font-semibold text-gray-900">
          Dienstplan {format(monthDate, 'MMMM yyyy', { locale: de })}
        </h2>
        <div className="flex space-x-3">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center px-4 py-2 neu-button bg-primary-50 text-primary-700"
          >
            <SafeIcon icon={FiDownload} className="w-5 h-5 mr-2" />
            PDF herunterladen
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 neu-button bg-orange-50 text-orange-700"
          >
            <SafeIcon icon={FiPrinter} className="w-5 h-5 mr-2" />
            Drucken
          </button>
        </div>
      </div>

      <div id="print-content" className="overflow-auto" ref={printRef}>
        <div className="print:hidden flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dienstplan</h1>
            <p className="text-xs text-gray-500">SGM Dienstplangenerator</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">
              Dienstplan {format(monthDate, 'MMMM yyyy', { locale: de })}
            </p>
            <p className="text-xs text-gray-500">
              Erstellt am {format(new Date(), 'dd.MM.yyyy', { locale: de })}
            </p>
          </div>
        </div>

        {mainReason && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg print:hidden">
            <p className="text-sm text-orange-800">
              <strong>Warum der Plan nicht optimal erstellt werden konnte:</strong> {mainReason}
            </p>
          </div>
        )}

        <div className="relative overflow-x-auto">
          <table className="min-w-full neu-card border-collapse print:border print:border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 sticky left-0 bg-white neu-element z-10 min-w-[200px] top-0 print:border print:border-gray-300">
                  Mitarbeiter
                </th>
                {days.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const understaffed = isUnderstaffed(day);
                  const wasOriginallyOmitted = (schedule.daysWithoutZwischendienst || []).includes(dateStr);
                  const isZwischenCurrentlyEmpty = !(schedule.schedule[dateStr]?.zwischen?.length > 0);
                  const showOmittedIndicator = wasOriginallyOmitted && isZwischenCurrentlyEmpty;

                  return (
                    <th
                      key={dateStr}
                      className={`px-1 py-1 text-center text-xxs font-medium text-gray-700 min-w-[24px] sticky top-0 print:border print:border-gray-300 ${
                        isWeekend(day) ? 'bg-blue-50' : 'bg-white'
                      } ${isHoliday(day) ? 'bg-orange-100' : ''} relative`}
                    >
                      <div>{format(day, 'dd')}</div>
                      <div className="text-xxs">{format(day, 'EE', { locale: de })}</div>
                      {understaffed ? (
                        <div
                          className="absolute bottom-0 left-1 right-1 h-0.5 bg-orange-500 rounded-full"
                          title="Mindestbesetzung nicht erreicht"
                        ></div>
                      ) : (
                        showOmittedIndicator && (
                          <div
                            className="absolute bottom-0 left-1 right-1 h-0.5 bg-gray-400 rounded-full"
                            title="Zwischendienst entfällt planmäßig"
                          ></div>
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => {
                // Calculate hours from the current schedule state using the corrected SHIFTS constant
                const totalHours = Object.entries(schedule.schedule).reduce((sum, [dateStr, daySchedule]) => {
                  const hasShift = SHIFTS.some(
                    (shift) => daySchedule[shift] && daySchedule[shift].includes(employee.id)
                  );
                  if (hasShift) {
                    const shiftType = SHIFTS.find(
                      (shift) => daySchedule[shift] && daySchedule[shift].includes(employee.id)
                    );
                    const duration =
                      shiftType === 'holiday'
                        ? (8 + 24 / 60) * (employee.workload / 100)
                        : settings.shifts[shiftType]?.hours + settings.shifts[shiftType]?.minutes / 60 || 0;
                    return sum + duration;
                  }
                  return sum;
                }, 0);

                const targetHoursCurrent = schedule.targetHours?.[employee.id] || 0;

                return (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2 whitespace-nowrap sticky left-0 bg-white neu-element z-10 print:border print:border-gray-300">
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <div className="w-6 h-6 neu-element flex items-center justify-center rounded-full">
                            <span className="text-xs font-medium text-orange-700">
                              {employee.name.split(' ').map((n) => n[0]).join('')}
                            </span>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-900">{employee.name}</span>
                        </div>
                        <div className="ml-8 mt-1">
                          <span
                            style={{ color: 'rgb(107, 114, 128)', fontSize: '12px', fontWeight: '300' }}
                            className="text-xxs text-gray-500"
                          >
                            {employee.workload}% | {formatHours(totalHours)} / {formatHours(targetHoursCurrent)}
                          </span>
                        </div>
                      </div>
                    </td>
                    {days.map((day) => {
                      const shift = getEmployeeShiftForDay(employee.id, day);
                      const preference = getEmployeePreferenceForDay(employee.id, day);
                      const isWeekendDay = isWeekend(day);
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const isOnHoliday = isHoliday(day);

                      const isOnSickLeave =
                        employee.sickLeave &&
                        employee.sickLeave.from &&
                        employee.sickLeave.to &&
                        dateStr >= employee.sickLeave.from &&
                        dateStr <= employee.sickLeave.to;

                      const isOnVacation =
                        employee.vacation &&
                        employee.vacation.from &&
                        employee.vacation.to &&
                        dateStr >= employee.vacation.from &&
                        dateStr <= employee.vacation.to;

                      return (
                        <td
                          key={dateStr}
                          className={`px-1 py-1 text-center print:border print:border-gray-300 ${
                            isWeekendDay ? 'bg-blue-50' : ''
                          } ${isOnHoliday && !shift ? 'bg-orange-100' : ''} cursor-pointer hover:bg-yellow-50`}
                          onClick={() => handleCellClick(employee.id, day)}
                        >
                          {isOnSickLeave ? (
                            <div className="w-6 h-6 mx-auto rounded-full bg-red-100 flex items-center justify-center neu-element-inset print:border print:border-red-200">
                              <span className="text-red-700 text-xxs font-bold">K</span>
                            </div>
                          ) : shift ? (
                            <div
                              className={`w-6 h-6 mx-auto rounded-full ${getShiftCellClass(
                                shift,
                                !!preference,
                                preference?.type
                              )} flex items-center justify-center neu-element-inset print:border print:border-gray-300 shadow-md relative`}
                            >
                              <span className="font-bold text-xs">{getShiftCode(shift)}</span>
                              {preference && (
                                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full">
                                  {preference.type === 'frei' && (
                                    <div
                                      className="w-2 h-2 bg-red-500 rounded-full"
                                      title="Freiwunsch: Frei"
                                    ></div>
                                  )}
                                  {preference.type === shift && (
                                    <div
                                      className="w-2 h-2 bg-green-500 rounded-full"
                                      title={`Freiwunsch: ${shift}`}
                                    ></div>
                                  )}
                                  {preference.type !== 'frei' && preference.type !== shift && (
                                    <div
                                      className="w-2 h-2 bg-orange-500 rounded-full"
                                      title={`Freiwunsch: ${preference.type}`}
                                    ></div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : isOnVacation ? (
                            <div className="w-6 h-6 mx-auto rounded-full bg-blue-100 flex items-center justify-center neu-element-inset print:border print:border-blue-200">
                              <span className="text-blue-700 text-xxs font-bold">U</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 mx-auto rounded-full bg-gray-100 flex items-center justify-center neu-element-inset print:border print:border-gray-300 relative">
                              {isOnHoliday ? (
                                <span className="text-orange-700 text-xxs font-bold">F</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                              {preference && (
                                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full">
                                  {preference.type === 'frei' && (
                                    <div
                                      className="w-2 h-2 bg-green-500 rounded-full"
                                      title="Freiwunsch: Frei (erfüllt)"
                                    ></div>
                                  )}
                                  {preference.type !== 'frei' && (
                                    <div
                                      className="w-2 h-2 bg-red-500 rounded-full"
                                      title={`Freiwunsch: ${preference.type} (nicht erfüllt)`}
                                    ></div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="print:visible print:table-header-group hidden">
              <tr>
                <td colSpan={days.length + 1} className="px-4 py-3 text-xs text-center text-gray-500">
                  Dienstplan | {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="bg-white rounded-xl p-6 neu-card print:mt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Legende</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-3">
            <h4 className="font-medium text-gray-800">Schichten</h4>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center neu-element-inset text-white font-bold">
                1
              </div>
              <span className="text-sm text-gray-700">
                Frühdienst ({settings.shifts.früh.start}-{settings.shifts.früh.end})
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center neu-element-inset text-white font-bold">
                2
              </div>
              <span className="text-sm text-gray-700">
                Zwischendienst ({settings.shifts.zwischen.start}-{settings.shifts.zwischen.end})
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center neu-element-inset text-white font-bold">
                3
              </div>
              <span className="text-sm text-gray-700">
                Spätdienst ({settings.shifts.spät.start}-{settings.shifts.spät.end})
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-800">Status</h4>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center neu-element-inset text-red-700 font-bold">
                K
              </div>
              <span className="text-sm text-gray-700">Krankheit</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center neu-element-inset text-blue-700 font-bold">
                U
              </div>
              <span className="text-sm text-gray-700">Urlaub/Ferien</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center neu-element-inset text-orange-700 font-bold">
                F
              </div>
              <span className="text-sm text-gray-700">Feiertag</span>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-800">Freiwünsche</h4>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center neu-element-inset relative">
                <span className="text-gray-600">-</span>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <span className="text-sm text-gray-700">Freiwunsch erfüllt</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center neu-element-inset relative">
                <span className="text-gray-600">-</span>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              </div>
              <span className="text-sm text-gray-700">Freiwunsch nicht erfüllt</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center neu-element-inset relative border-2 border-yellow-400">
                <span className="text-white font-bold">1</span>
              </div>
              <span className="text-sm text-gray-700">Gewünschte Schicht zugeteilt</span>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-800">Indikatoren</h4>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded relative">
                <span className="text-xs text-gray-700">01</span>
                <div className="absolute bottom-0 left-1 right-1 h-0.5 bg-orange-500 rounded-full"></div>
              </div>
              <span className="text-sm text-gray-700">Unterbesetzung</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded relative">
                <span className="text-xs text-gray-700">01</span>
                <div className="absolute bottom-0 left-1 right-1 h-0.5 bg-gray-400 rounded-full"></div>
              </div>
              <span className="text-sm text-gray-700">Zwischendienst entfällt</span>
            </div>
          </div>
        </div>
      </div>

      {showEmployeeModal && selectedCell && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neu-card bg-white max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Schicht zuweisen</h2>
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 neu-button"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Mitarbeiter:</strong> {employees.find((emp) => emp.id === selectedCell.employeeId)?.name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Datum:</strong> {format(selectedCell.date, 'dd.MM.yyyy', { locale: de })}
                </p>
                {(() => {
                  const preference = getEmployeePreferenceForDay(selectedCell.employeeId, selectedCell.date);
                  if (preference) {
                    return (
                      <p className="text-sm text-gray-700">
                        <strong>Freiwunsch:</strong>
                        <span
                          className={`ml-1 px-2 py-1 rounded text-xs ${
                            preference.type === 'frei' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {preference.type === 'frei' ? 'Frei' : `${preference.type}dienst`}
                        </span>
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-900">Schicht auswählen:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => assignShiftToEmployee('früh')}
                    className="p-3 neu-button bg-blue-50 text-blue-700 text-sm"
                  >
                    Frühdienst (1)
                  </button>
                  <button
                    onClick={() => assignShiftToEmployee('zwischen')}
                    className="p-3 neu-button bg-green-50 text-green-700 text-sm"
                  >
                    Zwischendienst (2)
                  </button>
                  <button
                    onClick={() => assignShiftToEmployee('spät')}
                    className="p-3 neu-button bg-purple-50 text-purple-700 text-sm"
                  >
                    Spätdienst (3)
                  </button>
                  {isHoliday(selectedCell.date) && (
                    <button
                      onClick={() => assignShiftToEmployee('holiday')}
                      className="p-3 neu-button bg-yellow-50 text-yellow-700 text-sm"
                    >
                      Feiertag (F)
                    </button>
                  )}
                  <button
                    onClick={() => assignShiftToEmployee('none')}
                    className="p-3 neu-button bg-gray-50 text-gray-700 text-sm"
                  >
                    Frei (-)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VerticalScheduleView;